import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase, SUPABASE_FUNCTIONS_URL } from '../../lib/supabase';
import { posthog } from '../../lib/posthog';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import { PaymentStep } from './PaymentStep';
import { AirlineLogo } from '../ui/AirlineLogo';
import { Button, Input, SectionLabel, StatusDot } from '@perch/ui';
import { getAirlineName } from '../../lib/constants';
import { getEmailProvider, type EmailProvider } from '../auth/emailUtils';
import type { User, Session } from '@supabase/supabase-js';
import type { UserProfile, Booking } from '../../lib/types';

const TRACK_EMAIL = 'track@perchtravel.com';

interface WelcomeFlowProps {
  user: User;
  session: Session;
  profile: UserProfile | null;
  onComplete: () => void;
  onAddManually?: () => void;
}

/**
 * Determine initial step based on profile state and URL params.
 *
 * Flow (reordered for card gate — Uber model):
 *   Step 0: Welcome + How It Works
 *   Step 1: Forward Confirmation Email (low friction first)
 *   Step 2: Add Payment Method — THE GATE (high commitment, after investment)
 *
 * Return visit logic:
 *   Has stripe_customer_id → onboarding complete (shouldn't be here)
 *   Has bookings but no card → Step 2 (card gate, urgency card)
 *   No bookings, no card → Step 0 or Step 1
 */
function resolveInitialStep(profile: UserProfile | null, hasBookings: boolean): number {
  // Card already on file — shouldn't be in WelcomeFlow at all, but handle gracefully
  if (profile?.stripe_customer_id) return 2;

  // Check URL params for Stripe redirect return
  const params = new URLSearchParams(window.location.search);
  if (params.has('checkout_session_id')) return 2; // will verify then advance
  if (params.has('checkout_cancelled')) return 2;

  // Has bookings but no card — return visitor who didn't finish. Go to card step.
  if (hasBookings) return 2;

  return 0;
}

export function WelcomeFlow({ user, session, profile, onComplete, onAddManually }: WelcomeFlowProps) {
  const { copied, copy: copyEmail } = useCopyToClipboard(TRACK_EMAIL);
  const [hasExistingBookings] = useState(() => false); // Will be updated if we detect existing bookings
  const [step, setStep] = useState(() => resolveInitialStep(profile, hasExistingBookings));
  const [bookingReceived, setBookingReceived] = useState(false);
  const [receivedBooking, setReceivedBooking] = useState<Partial<Booking> | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [setupComplete, setSetupComplete] = useState(false);
  const hasTrackedStart = useRef(false);
  const hasHandledRedirect = useRef(false);
  const autoAdvanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Name capture state — needed for magic link users who don't have OAuth metadata
  const needsNameCapture = !profile?.first_name;
  const [nameCaptured, setNameCaptured] = useState(false);
  const [nameFirst, setNameFirst] = useState('');
  const [nameLast, setNameLast] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameError, setNameError] = useState('');

  const firstName = profile?.first_name || user.user_metadata?.given_name || user.user_metadata?.full_name?.split(' ')[0] || '';

  // Provider detection for forwarding instructions
  const emailProvider = getEmailProvider(user.email ?? '');

  const cleanUrlParams = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete('checkout_session_id');
    url.searchParams.delete('checkout_cancelled');
    window.history.replaceState({}, '', url.pathname);
  }, []);

  const handleNameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nameFirst.trim()) {
      setNameError('First name is required.');
      return;
    }
    setNameSaving(true);
    setNameError('');
    const { error } = await supabase
      .from('user_profiles')
      .update({ first_name: nameFirst.trim(), last_name: nameLast.trim() || null })
      .eq('id', user.id);
    setNameSaving(false);
    if (error) {
      setNameError('Something went wrong. Please try again.');
      return;
    }
    posthog.capture('name_captured', { method: 'onboarding' });
    setNameCaptured(true);
  };

  // Track onboarding started + send welcome email
  useEffect(() => {
    if (!hasTrackedStart.current) {
      posthog.capture('onboarding_started');
      hasTrackedStart.current = true;

      // Fire welcome email (best-effort, deduped server-side)
      fetch(`${SUPABASE_FUNCTIONS_URL}/send-welcome-email`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      }).catch(() => {});
    }
  }, [session.access_token]);

  // Handle Stripe redirect return (Step 2 → verify → celebration)
  useEffect(() => {
    if (hasHandledRedirect.current) return;
    const params = new URLSearchParams(window.location.search);

    const checkoutSessionId = params.get('checkout_session_id');
    if (checkoutSessionId) {
      hasHandledRedirect.current = true;
      setVerifying(true);

      fetch(`${SUPABASE_FUNCTIONS_URL}/verify-checkout-session`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ session_id: checkoutSessionId }),
      })
        .then((res) => {
          if (!res.ok) throw new Error('Verification failed');
          return res.json();
        })
        .then(() => {
          posthog.capture('payment_step_completed');
          cleanUrlParams();
          // Card verified! Show celebration, then complete onboarding.
          setSetupComplete(true);
        })
        .catch((err) => {
          console.error('Checkout verification failed:', err);
          cleanUrlParams();
          setStep(2); // Stay on card step to retry
        })
        .finally(() => setVerifying(false));
      return;
    }

    if (params.has('checkout_cancelled')) {
      hasHandledRedirect.current = true;
      cleanUrlParams();
      setStep(2); // Stay on card step
    }
  }, [session.access_token, cleanUrlParams]);

  // Subscribe to Realtime for new bookings (Step 1 — email forwarding step)
  useEffect(() => {
    if (step !== 1) return;

    const channel = supabase
      .channel('welcome-booking-listener')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'tracked_bookings',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          setBookingReceived(true);
          if (payload.new) {
            setReceivedBooking(payload.new as Partial<Booking>);
          }
          // Auto-advance to card step after 3s (with manual button as primary path)
          autoAdvanceTimerRef.current = setTimeout(() => setStep(2), 3000);
        }
      )
      .subscribe();

    return () => {
      if (autoAdvanceTimerRef.current) clearTimeout(autoAdvanceTimerRef.current);
      supabase.removeChannel(channel);
    };
  }, [step, user.id]);

  // Celebration screen — setupComplete means both booking + card are done
  if (setupComplete) {
    return (
      <div className="min-h-screen bg-stone-50 dark:bg-stone-950 flex items-center justify-center px-4 sm:px-6">
        <div className="max-w-md w-full py-8 sm:py-16">
          <div className="text-center animate-in fade-in duration-500">
            {receivedBooking?.airline_code && (
              <div className="mx-auto mb-6 w-16 h-16">
                <AirlineLogo code={receivedBooking.airline_code} size={64} />
              </div>
            )}

            <h2 className="text-2xl sm:text-3xl font-serif text-stone-900 dark:text-stone-100 mb-2">
              Your First Flight Is Being Watched
            </h2>

            {receivedBooking?.airline_code && (
              <p className="text-sm font-mono text-stone-500 dark:text-stone-400 mb-1">
                {getAirlineName(receivedBooking.airline_code)}
                {receivedBooking.pnr && <> · {receivedBooking.pnr}</>}
              </p>
            )}

            <p className="text-stone-500 dark:text-stone-400 mt-3 mb-6">
              We'll take it from here.
            </p>

            {receivedBooking?.airline_code && (
              <div className="flex justify-center mb-6">
                <div className="w-48 h-5 flex items-center justify-center">
                  <StatusDot />
                </div>
              </div>
            )}

            <Button
              onClick={() => {
                posthog.capture('onboarding_completed', { method: 'full_setup' });
                onComplete();
              }}
              size="lg"
              className="focus:ring-2 focus:ring-[#FF6B35]/20"
              autoFocus
            >
              Go to Dashboard →
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950 flex items-center justify-center px-4 sm:px-6">
      <div className="max-w-md w-full py-8 sm:py-16" aria-live="polite">
        {verifying ? (
          // Verifying Stripe session
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-stone-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-stone-500"></span>
              </span>
              <span className="text-sm text-stone-500 dark:text-stone-400">
                Confirming your payment method&hellip;
              </span>
            </div>
          </div>
        ) : step === 0 ? (
          // Step 0: Welcome + How It Works
          <>
            <h1 className="text-3xl sm:text-4xl font-serif text-stone-900 dark:text-stone-100 mb-3">
              {firstName ? `Welcome, ${firstName}` : 'Welcome to Perch'}
            </h1>
            <p className="text-lg text-stone-500 dark:text-stone-400 mb-10">
              We handle the rebook. You pocket the difference.
            </p>

            <div className="space-y-5 mb-10">
              <Step number={1} title="Forward your confirmation" description="Search your inbox for your airline confirmation email and forward it to us." />
              <Step number={2} title="Add a payment method" description="We only charge when we save you money. 15% of what we find." />
              <Step number={3} title="We rebook, you save" description="When prices drop, we rebook at the lower fare. Same flight, same seat." />
            </div>

            <Button onClick={() => setStep(1)} size="lg" fullWidth>
              Continue
            </Button>
          </>
        ) : step === 1 && needsNameCapture && !nameCaptured ? (
          // Step 0.5: Name capture — magic link users only
          <form onSubmit={handleNameSubmit}>
            <h2 className="text-2xl sm:text-3xl font-serif text-stone-900 dark:text-stone-100 mb-2">
              One Last Thing
            </h2>
            <p className="text-stone-500 dark:text-stone-400 mb-8">
              So we know who to celebrate with when prices drop.
            </p>

            {nameError && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded-lg text-sm mb-4">
                {nameError}
              </div>
            )}

            <div className="space-y-3 mb-6">
              <Input
                type="text"
                value={nameFirst}
                onChange={(e) => setNameFirst(e.target.value)}
                placeholder="First name"
                autoComplete="given-name"
                aria-label="First name"
                aria-required="true"
                autoFocus
              />
              <Input
                type="text"
                value={nameLast}
                onChange={(e) => setNameLast(e.target.value)}
                placeholder="Last name (optional)"
                autoComplete="family-name"
                aria-label="Last name"
              />
            </div>

            <Button type="submit" disabled={nameSaving} size="lg" fullWidth>
              {nameSaving ? 'Saving...' : 'Continue'}
            </Button>
          </form>
        ) : step === 1 ? (
          // Step 1: Forward Email (with provider-specific instructions)
          <>
            <h2 className="text-2xl sm:text-3xl font-serif text-stone-900 dark:text-stone-100 mb-3">
              Forward Your Confirmation
            </h2>
            <p className="text-stone-500 dark:text-stone-400 mb-8">
              {emailProvider !== 'generic'
                ? <ProviderForwardingHint provider={emailProvider} />
                : 'Search your inbox for your airline confirmation email and forward it to the address below.'}
            </p>

            <div className="rounded-2xl border-2 border-stone-900 dark:border-stone-100 p-5 mb-6">
              <SectionLabel className="mb-3">
                Forward Confirmation Emails To
              </SectionLabel>
              <div className="flex items-center gap-3">
                <code className="flex-1 text-lg font-mono text-stone-900 dark:text-stone-100">
                  {TRACK_EMAIL}
                </code>
                <button
                  onClick={copyEmail}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-[border-color,box-shadow] active:scale-95 ${
                    copied
                      ? 'bg-emerald-500 text-white'
                      : 'bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 hover:opacity-90'
                  }`}
                >
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <div className="mt-4 pt-4 border-t border-stone-200 dark:border-stone-700">
                <p className="text-sm font-medium text-stone-700 dark:text-stone-300">
                  Works with Delta, United, and American.
                </p>
                <p className="text-xs text-stone-400 dark:text-stone-500 mt-1">
                  More airlines on the way.
                </p>
              </div>
            </div>

            {bookingReceived ? (
              // Quiet acknowledgment — booking received, advancing to card step
              <div className="animate-in fade-in duration-300">
                <div className="rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200/50 dark:border-emerald-800/30 p-4 mb-6" role="status">
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <div>
                      <span className="font-mono text-sm text-emerald-700 dark:text-emerald-300">
                        {receivedBooking?.airline_code && getAirlineName(receivedBooking.airline_code)}
                        {receivedBooking?.pnr && <> · {receivedBooking.pnr}</>}
                      </span>
                      <span className="text-sm text-emerald-600/80 dark:text-emerald-400/70 ml-2">
                        Received
                      </span>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={() => {
                    if (autoAdvanceTimerRef.current) clearTimeout(autoAdvanceTimerRef.current);
                    setStep(2);
                  }}
                  size="lg"
                  fullWidth
                >
                  Continue to Card Setup →
                </Button>
              </div>
            ) : (
              // Waiting for booking
              <>
                <div className="flex items-center gap-3 mb-2">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-stone-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-stone-500"></span>
                  </span>
                  <span className="text-sm text-stone-500 dark:text-stone-400">
                    Waiting for your first booking.
                  </span>
                </div>
                <p className="text-xs text-stone-400 dark:text-stone-500 ml-6 mb-8">
                  Usually takes under a minute.
                </p>

                {/* Add manually — only path if user doesn't want to forward email */}
                <button
                  onClick={() => {
                    onComplete();
                    onAddManually?.();
                  }}
                  className="text-sm text-stone-400 dark:text-stone-500 hover:text-stone-600 dark:hover:text-stone-300 transition-colors block py-2"
                >
                  or add manually →
                </button>

                {/* Dev-only: skip to card step for testing */}
                {!import.meta.env.PROD && (
                  <button
                    onClick={() => setStep(2)}
                    className="text-[10px] text-amber-500/50 hover:text-amber-500 transition-colors block py-1 mt-2"
                  >
                    [dev] skip to card step →
                  </button>
                )}
              </>
            )}
          </>
        ) : (
          // Step 2: Add Payment Method — THE GATE
          <PaymentStep
            session={session}
            pendingBooking={receivedBooking}
            onDevSkip={!import.meta.env.PROD ? () => {
              posthog.capture('onboarding_completed', { method: 'dev_skip' });
              onComplete();
            } : undefined}
          />
        )}
      </div>
    </div>
  );
}

function Step({ number, title, description }: { number: number; title: string; description: string }) {
  return (
    <div className="flex items-start gap-4">
      <span className="w-7 h-7 rounded-full bg-stone-200 dark:bg-stone-800 flex items-center justify-center text-xs font-medium text-stone-600 dark:text-stone-400 flex-shrink-0 mt-0.5">
        {number}
      </span>
      <div>
        <p className="text-sm font-medium text-stone-900 dark:text-stone-100">{title}</p>
        <p className="text-sm text-stone-500 dark:text-stone-400">{description}</p>
      </div>
    </div>
  );
}

const PROVIDER_FORWARDING_HINTS: Record<Exclude<EmailProvider, 'generic'>, string> = {
  gmail: 'Open your Gmail, find your airline confirmation, tap the forward arrow, and send it to the address below.',
  outlook: 'In Outlook, open your airline confirmation email, click Forward, and send it to the address below.',
  yahoo: 'In Yahoo Mail, open your booking confirmation, click Forward, and send it to the address below.',
  icloud: 'In iCloud Mail, open your airline confirmation, tap the reply arrow, choose Forward, and send to the address below.',
  protonmail: 'In Proton Mail, open your booking confirmation, click Forward, and send it to the address below.',
  fastmail: 'In Fastmail, open your airline confirmation, click Forward, and send it to the address below.',
};

function ProviderForwardingHint({ provider }: { provider: Exclude<EmailProvider, 'generic'> }) {
  return <>{PROVIDER_FORWARDING_HINTS[provider]}</>;
}
